import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import { Grid, Tab } from '@leapeasy/ui-kit';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo } from 'react';

import { getLandlordDetailAction } from 'store/actions/userActions';
import { failedNotif } from 'store/actions/notifActions';
import { openDetails } from 'store/actions/uiActions';
import { DetailCard } from './DetailCard';
import {
  ReportTab,
  ApplicationTab,
  PolicyTab,
  ClaimTab,
  CancellationsTab,
  BuildingTab,
} from './Tabs';
import { useDetailRouteTabs } from 'hooks/useDetailRouteTabs';
import { DetailPageShell, SectionPanelSkeleton, TabBarSkeleton } from 'components/DetailPageState';
import { useEntityDetailPageState } from 'hooks/useDetailPageState';
import { useImmutableJs } from 'hooks/useImmutableJs';
import { filterFlaggedCancellationDetailTabs } from 'utils/flaggedCancellationUi';

const INVITE_USER_TAB_SLUGS = [
  'report',
  'properties',
  'applications',
  'policy',
  'claim',
  'flagged-cancellation',
];

const TABS = [
  {
    title: 'Report',
    icon: 'General.Report',
  },
  {
    title: 'Properties',
    icon: 'EditorLayout.Apps',
  },
  {
    title: 'Applications',
    icon: 'HomePlaces.MapsHomeWork',
  },
  {
    title: 'Policy',
    icon: 'FileFolder.FindInPage',
  },
  {
    title: 'Claim',
    icon: 'HomePlaces.Gite',
  },
  // {
  //   title: 'Invoice',
  //   icon: 'FileFolder.Receipt',
  // },
  // {
  //   title: 'Details',
  //   icon: 'FileFolder.Plagiarism',
  // },
  {
    title: 'Flagged Cancellation',
    icon: 'General.Flag',
  },
];

const { tabSlugs: INVITE_USER_TAB_SLUGS_VISIBLE, tabs: INVITE_USER_TABS } =
  filterFlaggedCancellationDetailTabs(INVITE_USER_TAB_SLUGS, TABS);

export const InviteUserDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { landlordId } = useParams();

  const basePath = useMemo(
    () => (landlordId ? `/user/invite-new-user/${landlordId}/detail` : ''),
    [landlordId],
  );
  const { selectedIndex: selectedTab, goToTab } = useDetailRouteTabs({
    basePath,
    tabSlugs: INVITE_USER_TAB_SLUGS_VISIBLE,
  });

  const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));
  const details = useSelector((state) => state.getIn(['ui', 'details'])) || null;
  const detailsType = details ? details.get('type') : null;
  const detailsData = details ? details.get('data') : null;
  const landlordDetail = useSelector((state) => state.getIn(['user', 'landlord_detail'])) || null;
  const landlordDetailJSON = useImmutableJs(landlordDetail);
  const fallbackJSON = useImmutableJs(detailsData);
  const landlordDetailLoading = useSelector((state) =>
    state.getIn(['user', 'landlord_detail_loading']),
  );
  const landlordDetailError = useSelector((state) =>
    state.getIn(['user', 'landlord_detail_error']),
  );
  const hasUiFallback = !landlordId && detailsData && detailsType === 'landlord';
  const {
    data: building,
    isLoading,
    hasError,
  } = useEntityDetailPageState({
    entityId: landlordId,
    entityData: landlordDetailJSON ?? fallbackJSON,
    isLoading: landlordDetailLoading,
    hasError: landlordDetailError,
    hasUiFallback,
  });

  const onClickBack = useCallback(() => {
    dispatch(openDetails(null));
    navigate('/user/invite-new-user');
  }, [dispatch, navigate]);

  useEffect(() => {
    if (!landlordId) {
      if (!details || detailsType !== 'landlord') {
        dispatch(failedNotif('Operator ID is missing.'));
        onClickBack();
      }
      return;
    }
    dispatch(getLandlordDetailAction(landlordId));
  }, [dispatch, landlordId, details, detailsType, onClickBack]);

  const onRetry = useCallback(() => {
    if (landlordId) dispatch(getLandlordDetailAction(landlordId));
  }, [landlordId, dispatch]);

  return (
    <DashboardLayoutContainer shouldShowCard={false}>
      <DetailPageShell
        onBack={onClickBack}
        title={building?.name || null}
        isLoading={isLoading}
        hasError={hasError}
        onRetry={onRetry}
        loadingLabel="Loading operator details..."
        skeletonVariant="fields"
      >
        {building ? <DetailCard building={building} isDemo={isDemo} /> : null}
      </DetailPageShell>

      {isLoading ? (
        <Box
          padding="24px 12px"
          borderRadius="12px"
          border="1px solid #EAEAEA"
          mt={1}
          sx={{ background: 'white' }}
        >
          <TabBarSkeleton tabCount={INVITE_USER_TABS.length} />
          <SectionPanelSkeleton />
        </Box>
      ) : hasError || !building ? null : (
        <Box
          padding="24px 12px"
          borderRadius="12px"
          border="1px solid #EAEAEA"
          mt={1}
          sx={{ background: 'white' }}
        >
          <Grid>
            {INVITE_USER_TABS.map((tabItem, index) => (
              <Tab
                key={INVITE_USER_TAB_SLUGS_VISIBLE[index]}
                title={tabItem.title}
                icon={tabItem.icon}
                isSelected={selectedTab === index}
                onClick={() => goToTab(index)}
              />
            ))}
          </Grid>

          <Box marginTop="20px">
            {selectedTab === 0 && (
              <ReportTab building={building} onNavigateToApplications={() => goToTab(2)} />
            )}
            {selectedTab === 1 && <BuildingTab building={building} />}
            {selectedTab === 2 && <ApplicationTab building={building} />}
            {selectedTab === 3 && <PolicyTab building={building} />}
            {selectedTab === 4 && <ClaimTab building={building} />}
            {selectedTab === 5 && <CancellationsTab building={building} />}
          </Box>
        </Box>
      )}
    </DashboardLayoutContainer>
  );
};
